import api from './api';
import type { HydraCollection } from '../types/entities';
import type { Violation } from '../utils/mapViolations';
import type {
  FormularAB,
  FormularABFormData,
  FormularC,
  FormularCFormData,
  FormularD,
  FormularDFormData,
  EnvironmentalData,
  EnvironmentalDataFormData,
  AccidentData,
  AccidentDataFormData,
  ReportFilters,
} from '../types/reports';

// Helper to extract data from Hydra collection or plain array
function extractData<T>(data: HydraCollection<T> | T[]): T[] {
  if (Array.isArray(data)) {
    return data;
  }
  return data['hydra:member'] ?? [];
}

// Helper to build query params from filters.
//
// Exportiert (statt modulprivat), damit reportsApi.test.ts sie ohne Netzattrappe pruefen kann.
//
// Die Mehrwert-Form von `status` (Array statt Einzelwert) entspricht der `exact`-Strategie des
// API-Platform-`SearchFilter` gegen ein wiederholtes Feld -- mehrere `status[]=...`-Parameter
// werten serverseitig als SQL-`IN` aus (24-RESEARCH.md § Frage 1, empirisch gegen den laufenden
// Server geprueft, D-02 aus 24-CONTEXT.md). Der Typ musste dafuer zwingend mitwandern: ein
// durchgereichtes Array haette ohne die Array-Verzweigung ueber JS-`toString()` den Einzelstring
// "submitted,approved" ergeben -- ein Filter ohne Treffer und ohne Fehlermeldung (Fallstrick 1 aus
// 24-RESEARCH.md).
//
// D-17 (25-03): `reportingPeriodAfter`/`reportingPeriodBefore` erzeugen die Bereichs-Syntax auf
// demselben Feld (siehe die beiden `params.append`-Aufrufe unten). Die eckigen Klammern werden
// von `URLSearchParams.append` automatisch prozentkodiert (`%5Bafter%5D`) -- genau diese Syntax
// erwartet der in Plan 25-01 registrierte `DateFilter` auf allen fuenf Report-Collections. Das
// ist nicht optional: ein Parameter ohne registrierten Filter-Service wird von API Platform still
// ignoriert -- ohne Plan 25-01 saehe der Filter korrekt aus und wirkte trotzdem nicht.
// `year`/`period` werden hier NICHT ausgewertet -- sie sind reiner Anzeigezustand, die
// Umrechnung auf die Bereichsgrenzen macht der Aufrufer ueber `reportingPeriodRange`
// (frontend/src/utils/reportYearRange.ts).
export function buildQueryParams(filters?: ReportFilters): string {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.plant) {
    params.append('plant', String(filters.plant));
  }
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      for (const status of filters.status) {
        params.append('status[]', status);
      }
    } else {
      params.append('status', filters.status);
    }
  }
  // Exact-Match, bewusst NICHT entfernt: einziger Aufrufer ist `usePreviousValuesLoader.ts:208`,
  // das darueber die Vorjahreswerte laedt (`resolvePreviousReport()` nimmt anschliessend
  // `list[0]`). Ohne diesen Block kaeme eine beliebige Meldung des Werks als "Vorjahr" zurueck --
  // still, ohne Fehlermeldung.
  if (filters.reportingPeriod) {
    params.append('reportingPeriod', filters.reportingPeriod);
  }
  if (filters.reportingPeriodAfter) {
    params.append('reportingPeriod[after]', filters.reportingPeriodAfter);
  }
  if (filters.reportingPeriodBefore) {
    params.append('reportingPeriod[before]', filters.reportingPeriodBefore);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

// JEDER Statuswechsel laeuft ueber eine dedizierte Operation (Einreichen: D-03, Genehmigen:
// CR-01), NIEMALS ueber PATCH {status: ...} -- `status` steht nur in der Serialisierungsgruppe
// report:read, der Denormalizer verwarf das Feld stillschweigend (HTTP 200 ohne Statuswechsel,
// die Oberflaeche meldete trotzdem Erfolg).
//
// Die Aktions-URIs verwenden bewusst Bindestriche und weichen damit von den CRUD-Pfad-Konstanten
// (Unterstriche) ab -- die *_PATH-Konstanten sind hier nicht wiederverwendbar. Die
// *_ACTION_PATH-Konstante traegt nur den gemeinsamen Praefix, die Aktion haengt an der
// Aufrufstelle.

// FormularAB API
const FORMULAR_AB_PATH = '/api/formular_a_bs';
const FORMULAR_AB_ACTION_PATH = '/api/formular-ab';

export const formularABApi = {
  getAll: async (filters?: ReportFilters): Promise<FormularAB[]> => {
    const response = await api.get<HydraCollection<FormularAB> | FormularAB[]>(
      `${FORMULAR_AB_PATH}${buildQueryParams(filters)}`
    );
    return extractData(response.data);
  },

  getOne: async (id: number): Promise<FormularAB> => {
    const response = await api.get<FormularAB>(`${FORMULAR_AB_PATH}/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<FormularABFormData>): Promise<FormularAB> => {
    const response = await api.patch<FormularAB>(`${FORMULAR_AB_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  submit: async (id: number): Promise<FormularAB> => {
    const response = await api.post<FormularAB>(`${FORMULAR_AB_ACTION_PATH}/${id}/submit`, {});
    return response.data;
  },

  approve: async (id: number): Promise<FormularAB> => {
    const response = await api.post<FormularAB>(`${FORMULAR_AB_ACTION_PATH}/${id}/approve`, {});
    return response.data;
  },

  // Rueckgabe zur Ueberarbeitung (Phase 43, D-06): `reason` geht roh ans Backend, das trimmt und
  // bei leerem Text mit 422 (propertyPath `reason`) antwortet.
  returnForRevision: async (id: number, reason: string): Promise<FormularAB> => {
    const response = await api.post<FormularAB>(`${FORMULAR_AB_ACTION_PATH}/${id}/return-for-revision`, { reason });
    return response.data;
  },
};

// FormularC API
const FORMULAR_C_PATH = '/api/formular_cs';
const FORMULAR_C_ACTION_PATH = '/api/formular-c';

export const formularCApi = {
  getAll: async (filters?: ReportFilters): Promise<FormularC[]> => {
    const response = await api.get<HydraCollection<FormularC> | FormularC[]>(
      `${FORMULAR_C_PATH}${buildQueryParams(filters)}`
    );
    return extractData(response.data);
  },

  getOne: async (id: number): Promise<FormularC> => {
    const response = await api.get<FormularC>(`${FORMULAR_C_PATH}/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<FormularCFormData>): Promise<FormularC> => {
    const response = await api.patch<FormularC>(`${FORMULAR_C_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  submit: async (id: number): Promise<FormularC> => {
    const response = await api.post<FormularC>(`${FORMULAR_C_ACTION_PATH}/${id}/submit`, {});
    return response.data;
  },

  approve: async (id: number): Promise<FormularC> => {
    const response = await api.post<FormularC>(`${FORMULAR_C_ACTION_PATH}/${id}/approve`, {});
    return response.data;
  },

  // Rueckgabe zur Ueberarbeitung (Phase 43, D-06): `reason` geht roh ans Backend, das trimmt und
  // bei leerem Text mit 422 (propertyPath `reason`) antwortet.
  returnForRevision: async (id: number, reason: string): Promise<FormularC> => {
    const response = await api.post<FormularC>(`${FORMULAR_C_ACTION_PATH}/${id}/return-for-revision`, { reason });
    return response.data;
  },
};

// FormularD API
const FORMULAR_D_PATH = '/api/formular_ds';
const FORMULAR_D_ACTION_PATH = '/api/formular-d';

export const formularDApi = {
  getAll: async (filters?: ReportFilters): Promise<FormularD[]> => {
    const response = await api.get<HydraCollection<FormularD> | FormularD[]>(
      `${FORMULAR_D_PATH}${buildQueryParams(filters)}`
    );
    return extractData(response.data);
  },

  getOne: async (id: number): Promise<FormularD> => {
    const response = await api.get<FormularD>(`${FORMULAR_D_PATH}/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<FormularDFormData>): Promise<FormularD> => {
    const response = await api.patch<FormularD>(`${FORMULAR_D_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  submit: async (id: number): Promise<FormularD> => {
    const response = await api.post<FormularD>(`${FORMULAR_D_ACTION_PATH}/${id}/submit`, {});
    return response.data;
  },

  approve: async (id: number): Promise<FormularD> => {
    const response = await api.post<FormularD>(`${FORMULAR_D_ACTION_PATH}/${id}/approve`, {});
    return response.data;
  },

  // Rueckgabe zur Ueberarbeitung (Phase 43, D-06): `reason` geht roh ans Backend, das trimmt und
  // bei leerem Text mit 422 (propertyPath `reason`) antwortet.
  returnForRevision: async (id: number, reason: string): Promise<FormularD> => {
    const response = await api.post<FormularD>(`${FORMULAR_D_ACTION_PATH}/${id}/return-for-revision`, { reason });
    return response.data;
  },
};

// EnvironmentalData API
const ENVIRONMENTAL_DATA_PATH = '/api/environmental_datas';
const ENVIRONMENTAL_DATA_ACTION_PATH = '/api/environmental-data';

export const environmentalDataApi = {
  getAll: async (filters?: ReportFilters): Promise<EnvironmentalData[]> => {
    const response = await api.get<HydraCollection<EnvironmentalData> | EnvironmentalData[]>(
      `${ENVIRONMENTAL_DATA_PATH}${buildQueryParams(filters)}`
    );
    return extractData(response.data);
  },

  getOne: async (id: number): Promise<EnvironmentalData> => {
    const response = await api.get<EnvironmentalData>(`${ENVIRONMENTAL_DATA_PATH}/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<EnvironmentalDataFormData>): Promise<EnvironmentalData> => {
    const response = await api.patch<EnvironmentalData>(`${ENVIRONMENTAL_DATA_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  submit: async (id: number): Promise<EnvironmentalData> => {
    const response = await api.post<EnvironmentalData>(
      `${ENVIRONMENTAL_DATA_ACTION_PATH}/${id}/submit`,
      {}
    );
    return response.data;
  },

  approve: async (id: number): Promise<EnvironmentalData> => {
    const response = await api.post<EnvironmentalData>(
      `${ENVIRONMENTAL_DATA_ACTION_PATH}/${id}/approve`,
      {}
    );
    return response.data;
  },

  // Rueckgabe zur Ueberarbeitung (Phase 43, D-06): `reason` geht roh ans Backend, das trimmt und
  // bei leerem Text mit 422 (propertyPath `reason`) antwortet.
  returnForRevision: async (id: number, reason: string): Promise<EnvironmentalData> => {
    const response = await api.post<EnvironmentalData>(`${ENVIRONMENTAL_DATA_ACTION_PATH}/${id}/return-for-revision`, { reason });
    return response.data;
  },
};

// AccidentData API
const ACCIDENT_DATA_PATH = '/api/accident_datas';
const ACCIDENT_DATA_ACTION_PATH = '/api/accident-data';

export const accidentDataApi = {
  getAll: async (filters?: ReportFilters): Promise<AccidentData[]> => {
    const response = await api.get<HydraCollection<AccidentData> | AccidentData[]>(
      `${ACCIDENT_DATA_PATH}${buildQueryParams(filters)}`
    );
    return extractData(response.data);
  },

  getOne: async (id: number): Promise<AccidentData> => {
    const response = await api.get<AccidentData>(`${ACCIDENT_DATA_PATH}/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<AccidentDataFormData>): Promise<AccidentData> => {
    const response = await api.patch<AccidentData>(`${ACCIDENT_DATA_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  submit: async (id: number): Promise<AccidentData> => {
    const response = await api.post<AccidentData>(
      `${ACCIDENT_DATA_ACTION_PATH}/${id}/submit`,
      {}
    );
    return response.data;
  },

  approve: async (id: number): Promise<AccidentData> => {
    const response = await api.post<AccidentData>(
      `${ACCIDENT_DATA_ACTION_PATH}/${id}/approve`,
      {}
    );
    return response.data;
  },

  // Rueckgabe zur Ueberarbeitung (Phase 43, D-06): `reason` geht roh ans Backend, das trimmt und
  // bei leerem Text mit 422 (propertyPath `reason`) antwortet.
  returnForRevision: async (id: number, reason: string): Promise<AccidentData> => {
    const response = await api.post<AccidentData>(`${ACCIDENT_DATA_ACTION_PATH}/${id}/return-for-revision`, { reason });
    return response.data;
  },
};

/**
 * Ergebnis einer Payload-Validierung: zwei getrennte Meldungsstufen (D-09, Plan 20-06).
 *
 * `violations` blockiert (rote Feldmarkierung, Abbruch vor dem Einreichen), `warnings`
 * blockiert nichts -- es sind fachliche Plausibilitaetshinweise (K6/K9), die nur eine
 * bewusste Bestaetigung verlangen (D-11). Beide Listen tragen dasselbe Violation-Format
 * und werden mit demselben `mapViolations` auf Feldpfade abgebildet.
 */
export interface ReportValidationResult {
  violations: Violation[];
  warnings: Violation[];
}

/**
 * Validate a report form payload against the backend constraints without persisting.
 * Liefert beide Meldungsstufen (leere Listen = keine Beanstandung).
 *
 * `mode` steuert die Strenge der Backend-Validierung (D-02, Plan 20-02): 'submit'
 * validiert gegen ['Default', 'submit'] und schlaegt damit auch bei fehlenden
 * Pflichtfeldern an. Ohne `mode` bleibt es beim heutigen Default-Verhalten -- der
 * Schluessel wandert dann gar nicht erst in den Request-Body. Die Warnstufe ist von
 * `mode` unabhaengig: sie wird serverseitig immer separat erhoben.
 *
 * `reportId` benennt die bereits gespeicherte Meldung. Der Server schlaegt daran ihr Werk
 * nach, damit werksabhaengige Constraints hier dasselbe beurteilen wie beim Einreichen --
 * konkret die CSI-Ausnahme von der Klinker-/Zementproduktionspflicht (P-01). Uebermittelt
 * wird nur die Id: das Werk selbst und die CSI-Mitgliedschaft ermittelt ausschliesslich der
 * Server, ein Client kann sich die Ausnahme also nicht selbst zusprechen. Ohne `reportId`
 * bleibt es bei der reinen Payload-Pruefung.
 */
export const validateReport = async (
  type: string,
  data: Record<string, unknown>,
  mode?: string,
  reportId?: number
): Promise<ReportValidationResult> => {
  const response = await api.post<{ violations?: Violation[]; warnings?: Violation[] }>(
    '/api/reports/validate',
    {
      type,
      data,
      ...(mode ? { mode } : {}),
      ...(reportId ? { reportId } : {}),
    }
  );
  // Beide Listen defensiv auf [] normalisieren: `warnings` ist erst mit Plan 20-06
  // hinzugekommen, gegen einen aelteren Server fehlt der Schluessel schlicht. Ein
  // durchgereichtes undefined wuerde bei jedem Aufrufer (.length, mapViolations) knallen.
  return {
    violations: response.data.violations ?? [],
    warnings: response.data.warnings ?? [],
  };
};

/**
 * Calculate the derived values for a report form payload without persisting.
 * Returns the flat result object of the backend calculator (not wrapped, unlike validateReport).
 *
 * `reportId` gibt den Meldungskontext mit: Der Server liefert damit den Vergleichswert
 * Inlandsversand (Versand nach Produktkategorien und Zementartenstatistik) und die Differenz.
 */
export const calculateReport = async (
  type: string,
  data: Record<string, unknown>,
  reportId?: number
): Promise<Record<string, unknown>> => {
  const response = await api.post<Record<string, unknown>>('/api/reports/calculate', {
    type,
    data,
    ...(reportId ? { reportId } : {}),
  });
  return response.data;
};

// Combined reports API for fetching all report types
export const reportsApi = {
  getAllReports: async (filters?: ReportFilters): Promise<{
    formularAB: FormularAB[];
    formularC: FormularC[];
    formularD: FormularD[];
    environmental: EnvironmentalData[];
    accident: AccidentData[];
  }> => {
    const [formularAB, formularC, formularD, environmental, accident] = await Promise.all([
      formularABApi.getAll(filters),
      formularCApi.getAll(filters),
      formularDApi.getAll(filters),
      environmentalDataApi.getAll(filters),
      accidentDataApi.getAll(filters),
    ]);

    return { formularAB, formularC, formularD, environmental, accident };
  },

  getDraftCounts: async (): Promise<{
    formularAB: number;
    formularC: number;
    formularD: number;
    environmental: number;
    accident: number;
    total: number;
  }> => {
    const filters: ReportFilters = { status: 'draft' };
    const { formularAB, formularC, formularD, environmental, accident } = await reportsApi.getAllReports(filters);

    return {
      formularAB: formularAB.length,
      formularC: formularC.length,
      formularD: formularD.length,
      environmental: environmental.length,
      accident: accident.length,
      total: formularAB.length + formularC.length + formularD.length + environmental.length + accident.length,
    };
  },
};
